import { createContext, useContext, useState } from 'react';
import { buildApiUrl } from '../config/api';
import * as React from 'react';
import { AuthTokenSuccessResponse } from '../types/global';
import { pushNotificationService } from '../services/pushNotificationService';
import { gtmService } from '../services/gtmService';

interface ProviderProps {
    user: User;
    token: string;
    credentialType: number | null;
    /** False only while validating a stored session after load; dashboards should wait before redirecting unauthenticated users. */
    authSessionReady: boolean;
    isLogin: () => boolean;
    login: (data: AuthTokenSuccessResponse & { id?: number }) => void;
    logout: () => void;
    updateUser: (updatedUser: Partial<User>) => void;
    validateToken: (tokenOverride?: string) => Promise<boolean>;
}

interface User {
    id: number | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    access_token: string | null;
    token_type: string | null;
    credential_type: number | null;
}

const initialUser: User = {
    id: null,
    firstName: null,
    lastName: null,
    email: null,
    access_token: null,
    token_type: null,
    credential_type: null,
};

const AuthContext = createContext<ProviderProps>({
    user: initialUser,
    token: '',
    credentialType: null,
    authSessionReady: true,
    isLogin: () => false,
    login: () => {},
    logout: () => {},
    updateUser: () => {},
    validateToken: async () => false,
});

export function useAuth() {
    return useContext(AuthContext);
}

// Helper function to convert credential type to number
const convertCredentialTypeToNumber = (credentialType: any): number | null => {
    if (typeof credentialType === 'string') {
        switch (credentialType) {
            case 'customer': return 1;
            case 'worker': return 2;
            case 'admin': return 3;
            case 'manager': return 4;
            default: return null;
        }
    }
    const n = Number(credentialType);
    return Number.isFinite(n) ? n : null;
};

function storedUserNeedsSessionValidation(stored: unknown): boolean {
    if (!stored || typeof stored !== 'object') {
        return false;
    }
    const rec = stored as Record<string, unknown>;
    const tok = rec.access_token;
    const email = rec.email;
    return typeof tok === 'string' && tok.length > 0 && typeof email === 'string' && email.length > 0;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const storedInfo = localStorage.getItem('user')
        ? JSON.parse(localStorage.getItem('user')!)
        : null;

    const initialCredentialType =
        storedInfo != null && storedInfo.credential_type !== undefined && storedInfo.credential_type !== null
            ? convertCredentialTypeToNumber(storedInfo.credential_type)
            : null;

    const [user, setUser] = useState<User>(storedInfo ? {
        id: storedInfo.id || null,
        firstName: storedInfo.first_name,
        lastName: storedInfo.last_name,
        email: storedInfo.email,
        access_token: storedInfo.access_token,
        token_type: storedInfo.token_type,
        credential_type: initialCredentialType
    } : initialUser);

    const [token, setToken] = useState(storedInfo?.access_token || '');
    const [credentialType, setCredentialType] = useState<number | null>(initialCredentialType);

    const [authSessionReady, setAuthSessionReady] = useState<boolean>(
        () => !storedUserNeedsSessionValidation(storedInfo)
    );

    const validateToken = async (tokenOverride?: string): Promise<boolean> => {
        const t = tokenOverride ?? token;
        if (!t || t === '') {
            return false;
        }

        try {
            // Make a simple API call to validate the token
            const response = await fetch(buildApiUrl('/api/credential/type'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${t}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                return true;
            } else if (response.status === 401) {
                // Token is invalid, clear authentication state (await so bootstrap finishes after session is cleared)
                await logout();
                return false;
            } else {
                // Other error (500, 404, etc.), assume token is still valid to avoid unnecessary logouts
                return true;
            }
        } catch (error) {
            // On network error, assume token is still valid to avoid unnecessary logouts
            return true;
        }
    };

    React.useEffect(() => {
        if (authSessionReady) {
            return;
        }

        let cancelled = false;
        const initialToken = token;
        const initialEmail = user.email;
        const initialAccess = user.access_token;

        const runBootstrap = async () => {
            try {
                await new Promise<void>((r) => {
                    setTimeout(r, 50);
                });
                if (cancelled) {
                    return;
                }
                if (initialToken && initialEmail && initialAccess) {
                    try {
                        await validateToken(initialToken);
                    } catch {
                        // validateToken handles 401; ignore unexpected errors
                    }
                }
            } finally {
                if (!cancelled) {
                    setAuthSessionReady(true);
                }
            }
        };

        void runBootstrap();

        return () => {
            cancelled = true;
        };
        // Intentionally once on mount for the stored session loaded into initial state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    const login = (data: AuthTokenSuccessResponse & { id?: number }) => {
        const credType = convertCredentialTypeToNumber(data.credential_type);
        const userData = {
            id: data.id || null,
            firstName: data.first_name,
            lastName: data.last_name,
            email: data.email,
            access_token: data.access_token,
            token_type: data.token_type,
            credential_type: credType
        };

        setUser(userData);
        setToken(data.access_token);
        setCredentialType(credType);
        
        const localStorageData = {
            id: data.id,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            access_token: data.access_token,
            token_type: data.token_type,
            credential_type: data.credential_type
        };
        
        localStorage.setItem('user', JSON.stringify(localStorageData));
        
        // Register any pending push notification token now that user is logged in
        if (pushNotificationService.isAvailable()) {
            pushNotificationService.registerPendingToken();
        }

        // Track login event in GTM
        const userType = data.credential_type === 1 ? 'customer' : 
                        data.credential_type === 2 ? 'worker' : 
                        data.credential_type === 3 ? 'admin' : 
                        data.credential_type === 4 ? 'manager' : 'customer';
        gtmService.trackUserLogin(userType, data.id);
        gtmService.setUserProperties({
            userId: data.id,
            userType: userType,
            email: data.email,
        });
    };

    const logout = async () => {
        const prevUser = user;
        const prevCredentialType = credentialType;

        // Track logout event in GTM before clearing user data
        if (prevUser.id && prevCredentialType !== null) {
            const userType = prevCredentialType === 1 ? 'customer' :
                            prevCredentialType === 2 ? 'worker' :
                            prevCredentialType === 3 ? 'admin' :
                            prevCredentialType === 4 ? 'manager' : undefined;
            gtmService.trackUserLogout(userType, prevUser.id);
            gtmService.clearUserProperties();
        }

        // Clear session immediately so route guards and API clients see logged-out state
        setUser(initialUser);
        setToken('');
        setCredentialType(null);
        localStorage.removeItem('user');

        try {
            window.dispatchEvent(new CustomEvent('user-logged-out'));
        } catch (error) {
            console.debug('Could not dispatch logout event:', error);
        }

        // Best-effort push/badge cleanup (no longer depends on auth token in UI state)
        try {
            const { PushNotificationService } = await import('../services/pushNotificationService');
            const pushService = PushNotificationService.getInstance();
            await pushService.unregister();
        } catch (error) {
            console.debug('Could not unregister push notifications on logout:', error);
        }

        try {
            const { Capacitor } = await import('@capacitor/core');
            if (Capacitor.getPlatform() === 'ios') {
                const { Badge } = await import('@capawesome/capacitor-badge');
                await Badge.clear();
            }
        } catch (error) {
            console.debug('Could not clear badge on logout:', error);
        }
    };

    const isLogin = () => {
        const hasToken = token !== '' && token !== null && token !== undefined;
        const hasUserData = user && user.email && user.access_token;
        const hasCredentialType = credentialType !== null && credentialType !== undefined;
        
        return Boolean(hasToken && hasUserData && hasCredentialType);
    };

    const updateUser = (updatedUser: Partial<User>) => {
        const newUser = { ...user, ...updatedUser };
        setUser(newUser);

        // Update localStorage to persist changes
        localStorage.setItem('user', JSON.stringify({
            id: newUser.id,
            first_name: newUser.firstName,
            last_name: newUser.lastName,
            email: newUser.email,
            access_token: newUser.access_token,
            token_type: newUser.token_type,
            credential_type: newUser.credential_type
        }));
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            credentialType,
            authSessionReady,
            isLogin,
            login,
            logout,
            updateUser,
            validateToken
        }}>
            {children}
        </AuthContext.Provider>
    );
}
